import { useEffect, useMemo, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  ICirculation,
  ICirculationGridFilter,
  ICirculationGridFilterSchema,
} from "../api/types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm } from "react-hook-form";
import { IDropdownModel } from "common/models/IDropdownModel";
import DatePickerElement from "components/DatePickerElement";
import dayjs from "dayjs";
import { GetMaterialTypeValues } from "features/materialtype/api/hooks";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import useDashboardStore from "../store/DashboardStore";
import { GetCirculation } from "../api/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectField } from "components/@extended/SelectField";
import StyledDataGrid from "components/@extended/StyledDataGrid";

const CirculationGrid = () => {
  const {
    circulationPostModel,
    handleCirculationFiltering,
    handleCirculationPagination,
    handleCirculationSorting,
  } = useDashboardStore();

  const { data: getCirculations, isFetching } =
    GetCirculation(circulationPostModel);

  const columns: GridColDef<ICirculation>[] = useMemo(
    () => [
      {
        field: "materialTypeName",
        headerName: "Material Name",
        flex: 1,
      },
      {
        field: "today",
        headerName: "Today",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <>
            <span style={{ color: "red" }} title="Today Issue">
              {params.row?.todayIssueCount}
            </span>
            <span> | </span>
            <span style={{ color: "green" }} title="Today Return">
              {params.row?.todayReturnCount}
            </span>
          </>
        ),
      },
      {
        field: "lastWeek",
        headerName: "Last Week",
        headerAlign: "center",
        flex: 1,
        align: "center",
        renderCell: (params) => (
          <>
            <span
              style={{ color: "red", cursor: "pointer" }}
              title="Last Week Issued Count"
            >
              {params.row.lastWeekIssueCount}
            </span>
            <span> | </span>
            <span
              style={{ color: "green", cursor: "pointer" }}
              title="Last Week Return Count"
            >
              {params.row.lastWeekReturnCount}
            </span>
          </>
        ),
      },
      {
        field: "last30Days",
        headerName: "Last 30 Days",
        headerAlign: "center",
        flex: 1,
        align: "center",
        renderCell: (params) => (
          <>
            <span
              style={{ color: "red", cursor: "pointer" }}
              title="Last Month Issued Count"
            >
              {params.row.last30DaysIssueCount}
            </span>
            <span> | </span>
            <span
              style={{ color: "green", cursor: "pointer" }}
              title="Last Month Return Count"
            >
              {params.row.last30DaysReturnCount}
            </span>
          </>
        ),
      },
      {
        field: "filterDate",
        headerName: "From Date  -  To Date",
        headerAlign: "center",
        flex: 1,
        align: "center",
        renderCell: (params) => (
          <>
            <span
              style={{ color: "red", cursor: "pointer" }}
              title="Filtered Date Issued Count"
            >
              {params.row.filterDateIssueCount}
            </span>
            <span> | </span>
            <span
              style={{ color: "green", cursor: "pointer" }}
              title="Filtered Date Return Count"
            >
              {params.row.filterDateReturnCount}
            </span>
          </>
        ),
      },
    ],
    [],
  );

  const methods = useForm<ICirculationGridFilter>({
    mode: "all",
    resolver: zodResolver(ICirculationGridFilterSchema),
  });

  const { reset, handleSubmit, control, watch, setValue } = methods;

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  useEffect(() => {
    if (fromDate && toDate) {
      const from = dayjs(fromDate);
      const to = dayjs(toDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        setValue("toDate", fromDate);
      }
    }
  }, [fromDate, toDate, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    handleCirculationFiltering(data);
  });

  const handleClear = () => {
    handleCirculationFiltering({
      materialTypeID: null,
      memberTypeID: null,
      fromDate: dayjs().subtract(6, "month").toString(),
      toDate: dayjs().toString(),
    });
  };

  useEffect(() => {
    reset(circulationPostModel?.filterModel);
  }, [circulationPostModel?.filterModel, reset]);

  const rowCountRef = useRef(getCirculations?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (getCirculations?.apiData?.count !== undefined) {
      rowCountRef.current = getCirculations?.apiData?.count;
    }
    return rowCountRef.current;
  }, [getCirculations?.apiData?.count]);

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: materialTypeQuery } = GetMaterialTypeValues();
  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMaterialTypeOptions(materialTypeQuery?.apiData || []);
  }, [materialTypeQuery]);

  return (
    <>
      <Box component="form" onSubmit={onSubmit}>
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h3" component={"h3"} marginBlockEnd={2}>
              Circulations
              <Divider sx={{ my: 1 }} />
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <DatePickerElement
                  control={control}
                  name="fromDate"
                  label="From Date"
                  format="DD-MM-YYYY"
                  maxDate={
                    toDate && dayjs(toDate).isValid()
                      ? dayjs(toDate)
                      : undefined
                  }
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <DatePickerElement
                  control={control}
                  name="toDate"
                  label="To Date"
                  format="DD-MM-YYYY"
                  minDate={
                    fromDate && dayjs(fromDate).isValid()
                      ? dayjs(fromDate)
                      : undefined
                  }
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={memberTypeOptions}
                  control={control}
                  name="memberTypeID"
                  label="Member Type"
                  placeholder="Select Member Type"
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={materialTypeOptions}
                  control={control}
                  name="materialTypeID"
                  label="Material Type"
                  placeholder="Select Material Type"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3} sx={{ mt: 3 }}>
                <LoadingButton
                  variant="contained"
                  sx={{ mr: 1 }}
                  type="submit"
                  loading={isFetching}
                  disabled={isFetching}
                >
                  Search
                </LoadingButton>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClear}
                  disabled={isFetching}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Grid container direction={"row"} spacing={2}>
        <Grid item xs={12} md={12}>
          <Card elevation={3} sx={{ p: 3 }}>
            <Grid sx={{ display: "flex", justifyContent: "end", mb: 3 }}>
              <Grid
                justifyContent={"end"}
                container
                alignItems="center"
                maxWidth={"150px"}
              >
                <Box
                  sx={{
                    backgroundColor: "red",
                    padding: "4px",
                    color: "white",
                    borderRadius: "30%",
                    textAlign: "center",
                  }}
                ></Box>
                <Typography color={"red"} sx={{ paddingLeft: "3px" }}>
                  - Issued items
                </Typography>
              </Grid>
              <Grid
                justifyContent={"end"}
                container
                alignItems="center"
                maxWidth={"150px"}
              >
                <Box
                  sx={{
                    backgroundColor: "green",
                    padding: "4px",
                    color: "white",
                    borderRadius: "30%",
                    textAlign: "center",
                  }}
                ></Box>
                <Typography color={"green"} sx={{ paddingLeft: "3px" }}>
                  - Return items
                </Typography>
              </Grid>
            </Grid>
            <Grid container sx={{ height: 450 }}>
              <StyledDataGrid
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "wrap",
                    fontWeight: 600,
                    lineHeight: "normal",
                    fontSize: "13px",
                  },
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "wrap",
                    alignContent: "center",
                    fontSize: 14,
                  },
                }}
                density="compact"
                disableRowSelectionOnClick
                getRowId={(item) => item.uniqueID}
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { ...circulationPostModel.pagingModel },
                  },
                }}
                sortModel={circulationPostModel.sortModel}
                onSortModelChange={handleCirculationSorting}
                onPaginationModelChange={handleCirculationPagination}
                getRowHeight={() => "auto"}
                paginationMode="server"
                sortingMode="server"
                rows={getCirculations?.apiData?.data}
                rowCount={rowCount}
                disableColumnFilter
                disableColumnSorting
                loading={isFetching}
              />
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default CirculationGrid;
